/**
 * 薬局シフト管理 mock の読み書き層（Dexie repository）。
 * route -query / -hooks はこの module を経由して DemoDB にアクセスする。
 */
import { db } from './schema';
import { DEMO_SHIFT_ANCHOR, DEMO_SHIFT_MONTH0, DEMO_SHIFT_MONTH_PREFIX, DEMO_SHIFT_YEAR } from './seeds/pharmacyShift';
import type { PharmacyArea, PharmacyStore, ShiftAlert, ShiftAlertType, ShiftAssignment, ShiftPosition, ShiftRequestDraft, ShiftStaff, ShiftUnitType } from './types';
export { DEMO_SHIFT_ANCHOR, DEMO_SHIFT_MONTH0, DEMO_SHIFT_MONTH_PREFIX, DEMO_SHIFT_YEAR };

/** 職種の日本語ラベル（SchemaTableRow.attributes 等、データとして書き込む箇所で使用）。 */
const POSITION_LABEL_JA: Record<ShiftPosition, string> = {
  pharmacist: '薬剤師',
  registeredSeller: '登録販売者',
  cashier: 'レジ',
  cosmetics: '化粧品',
  stocker: '品出し'
};

// ─── アラート集計 ────────────────────────────────────────────
export type ShiftAlertCounts = {
  total: number;
  critical: number;
  warning: number;
  byType: Record<ShiftAlertType, number>;
};
function emptyAlertCounts(): ShiftAlertCounts {
  return {
    total: 0,
    critical: 0,
    warning: 0,
    byType: {
      unqualified_sales_risk: 0,
      understaffed: 0,
      labor_cost_over: 0,
      overtime_over: 0
    }
  };
}
function accumulateAlert(counts: ShiftAlertCounts, alert: ShiftAlert): void {
  counts.total += 1;
  if (alert.severity === 'critical') counts.critical += 1;else counts.warning += 1;
  counts.byType[alert.type] += 1;
}

// ─── 一覧の行（拠点 or エリア） ──────────────────────────────
export type ShiftUnitRow = {
  unitType: ShiftUnitType;
  /** storeId（place）または areaId（orgUnit）。 */
  id: string;
  name: string;
  /** 店舗コード（place のみ。area は空文字）。 */
  code: string;
  /** 所属エリア（place のみ。orgUnit 行のドリル元として使用）。 */
  areaId: string;
  areaName: string;
  /** 配下店舗数（area=集約数 / store=1）。 */
  storeCount: number;
  staffCount: number;
  /** 月次の延べ労働時間（全スタッフ・休憩控除後の人時）。 */
  plannedHours: number;
  /** 営業時間中に有資格者（薬剤師/登販）が在席するスロット割合（0–1）。 */
  qualifiedCoverageRate: number;
  /** 営業時間中に必要人数を満たすスロット割合（0–1）。 */
  fillRate: number;
  /** 月次の人件費（円）。 */
  laborCost: number;
  /** 月次の人件費予算（円）。 */
  laborBudget: number;
  /** 人件費 / 予算（1.0 超で予算超過）。 */
  laborRatio: number;
  alertCounts: ShiftAlertCounts;
};

/** assignment 1 件の実働時間（休憩控除）。 */
function workedHours(a: ShiftAssignment): number {
  const span = a.endH - a.startH;
  const brk = a.breakStartH != null && a.breakEndH != null ? a.breakEndH - a.breakStartH : 0;
  return Math.max(0, span - brk);
}
type StoreMetrics = {
  staffCount: number;
  plannedHours: number;
  laborCost: number;
  laborBudget: number;
  /** 営業時間スロット総数（営業日数 × 営業時間/日、1 時間=1 スロット）。 */
  totalSlots: number;
  /** 有資格者（薬剤師/登販）が 1 名以上いるスロット数。 */
  qualifiedSlots: number;
  /** 必要人数を満たすスロット数。 */
  filledSlots: number;
  alertCounts: ShiftAlertCounts;
};

/** monthPrefix "YYYY-MM" → その月の日数。 */
function daysInMonthPrefix(monthPrefix: string): number {
  const [y, m] = monthPrefix.split('-').map(Number);
  return new Date(Date.UTC(y ?? 2026, m ?? 1, 0)).getUTCDate();
}

/** 第1類/第2類を販売できる有資格者か（薬剤師 or 登録販売者でライセンス保有）。 */
function canSellOtc(staff: ShiftStaff): boolean {
  return staff.qualified && (staff.position === 'pharmacist' || staff.position === 'registeredSeller');
}

/** assignment が時刻 h（[h, h+1) スロット）をカバーし、かつ休憩中でないか。 */
function coversHour(a: ShiftAssignment, h: number): boolean {
  if (h < a.startH || h >= a.endH) return false;
  if (a.breakStartH != null && a.breakEndH != null) {
    if (h >= a.breakStartH && h < a.breakEndH) return false;
  }
  return true;
}
async function computeStoreMetrics(store: PharmacyStore, staffByStore: Map<string, ShiftStaff[]>, assignmentsByStore: Map<string, ShiftAssignment[]>, alertsByStore: Map<string, ShiftAlert[]>, monthPrefix: string): Promise<StoreMetrics> {
  const staff = staffByStore.get(store.id) ?? [];
  const staffById = new Map(staff.map(s => [s.id, s]));
  const assignments = assignmentsByStore.get(store.id) ?? [];
  let plannedHours = 0;
  let laborCost = 0;
  for (const a of assignments) {
    const h = workedHours(a);
    plannedHours += h;
    laborCost += h * (staffById.get(a.staffId)?.hourlyWage ?? 0);
  }

  // 営業時間カバレッジ: 営業日 × 営業時間スロットごとに、在席人数と有資格者の有無を判定。
  const days = daysInMonthPrefix(monthPrefix);
  const byDate = groupBy(assignments, a => a.date);
  let totalSlots = 0;
  let qualifiedSlots = 0;
  let filledSlots = 0;
  for (let day = 1; day <= days; day++) {
    const date = `${monthPrefix}-${String(day).padStart(2, '0')}`;
    const dayAssignments = byDate.get(date) ?? [];
    for (let h = store.openHour; h < store.closeHour; h++) {
      totalSlots += 1;
      let headcount = 0;
      let qualifiedPresent = false;
      for (const a of dayAssignments) {
        if (!coversHour(a, h)) continue;
        headcount += 1;
        const member = staffById.get(a.staffId);
        if (member && canSellOtc(member)) qualifiedPresent = true;
      }
      if (qualifiedPresent) qualifiedSlots += 1;
      if (headcount >= store.requiredHeadcount) filledSlots += 1;
    }
  }
  const counts = emptyAlertCounts();
  for (const alert of alertsByStore.get(store.id) ?? []) {
    if (alert.status === 'resolved') continue;
    accumulateAlert(counts, alert);
  }
  return {
    staffCount: staff.length,
    plannedHours,
    laborCost,
    laborBudget: store.monthlyLaborBudget,
    totalSlots,
    qualifiedSlots,
    filledSlots,
    alertCounts: counts
  };
}

/** 一覧データを単位種別ごとに取得。place=店舗1行 / orgUnit=エリア1行（配下店舗を集約）。 */
export async function listShiftUnits(params: {
  unitType: ShiftUnitType;
  /** 集計対象月 "YYYY-MM"（未指定はデモ基準月）。 */
  monthPrefix?: string;
}): Promise<ShiftUnitRow[]> {
  const monthPrefix = params.monthPrefix ?? DEMO_SHIFT_MONTH_PREFIX;
  const [areas, stores, staff, assignments, alerts] = await Promise.all([db.pharmacyAreas.toArray(), db.pharmacyStores.toArray(), db.shiftStaff.toArray(), db.shiftAssignments.filter(a => a.date.startsWith(monthPrefix)).toArray(), db.shiftAlerts.toArray()]);
  const staffByStore = groupBy(staff, s => s.storeId);
  const assignmentsByStore = groupBy(assignments, a => a.storeId);
  const alertsByStore = groupBy(alerts, a => a.storeId);
  const areaById = new Map(areas.map(a => [a.id, a]));
  const sortedStores = [...stores].sort((a, b) => a.sortOrder - b.sortOrder);
  const metricsByStore = new Map<string, StoreMetrics>();
  for (const store of sortedStores) {
    metricsByStore.set(store.id, await computeStoreMetrics(store, staffByStore, assignmentsByStore, alertsByStore, monthPrefix));
  }
  if (params.unitType === 'place') {
    return sortedStores.map(store => {
      const m = metricsByStore.get(store.id) ?? {
        staffCount: 0,
        plannedHours: 0,
        laborCost: 0,
        laborBudget: store.monthlyLaborBudget,
        totalSlots: 0,
        qualifiedSlots: 0,
        filledSlots: 0,
        alertCounts: emptyAlertCounts()
      };
      const area = areaById.get(store.areaId);
      return {
        unitType: 'place' as const,
        id: store.id,
        name: store.name,
        code: store.code,
        areaId: store.areaId,
        areaName: area?.name ?? '',
        storeCount: 1,
        staffCount: m.staffCount,
        plannedHours: m.plannedHours,
        qualifiedCoverageRate: m.totalSlots > 0 ? m.qualifiedSlots / m.totalSlots : 0,
        fillRate: m.totalSlots > 0 ? m.filledSlots / m.totalSlots : 0,
        laborCost: m.laborCost,
        laborBudget: m.laborBudget,
        laborRatio: m.laborBudget > 0 ? m.laborCost / m.laborBudget : 0,
        alertCounts: m.alertCounts
      };
    });
  }

  // orgUnit: エリア単位で集約
  const sortedAreas = [...areas].sort((a, b) => a.sortOrder - b.sortOrder);
  return sortedAreas.map(area => {
    const areaStores = sortedStores.filter(s => s.areaId === area.id);
    const agg = emptyAlertCounts();
    let staffCount = 0;
    let plannedHours = 0;
    let laborCost = 0;
    let laborBudget = 0;
    let totalSlots = 0;
    let qualifiedSlots = 0;
    let filledSlots = 0;
    for (const store of areaStores) {
      const m = metricsByStore.get(store.id);
      if (!m) continue;
      staffCount += m.staffCount;
      plannedHours += m.plannedHours;
      laborCost += m.laborCost;
      laborBudget += m.laborBudget;
      totalSlots += m.totalSlots;
      qualifiedSlots += m.qualifiedSlots;
      filledSlots += m.filledSlots;
      agg.total += m.alertCounts.total;
      agg.critical += m.alertCounts.critical;
      agg.warning += m.alertCounts.warning;
      agg.byType.unqualified_sales_risk += m.alertCounts.byType.unqualified_sales_risk;
      agg.byType.understaffed += m.alertCounts.byType.understaffed;
      agg.byType.labor_cost_over += m.alertCounts.byType.labor_cost_over;
      agg.byType.overtime_over += m.alertCounts.byType.overtime_over;
    }
    return {
      unitType: 'orgUnit' as const,
      id: area.id,
      name: area.name,
      code: '',
      areaId: area.id,
      areaName: area.name,
      storeCount: areaStores.length,
      staffCount,
      plannedHours,
      qualifiedCoverageRate: totalSlots > 0 ? qualifiedSlots / totalSlots : 0,
      fillRate: totalSlots > 0 ? filledSlots / totalSlots : 0,
      laborCost,
      laborBudget,
      laborRatio: laborBudget > 0 ? laborCost / laborBudget : 0,
      alertCounts: agg
    };
  });
}

// ─── 店舗詳細 ────────────────────────────────────────────────
export type StoreShiftDetail = {
  store: PharmacyStore;
  area: PharmacyArea | null;
  staff: ShiftStaff[];
  assignments: ShiftAssignment[];
};

/** 店舗の基本情報 + スタッフ + 当月の割当をまとめて返す。 */
export async function getStoreShiftDetail(storeId: string): Promise<StoreShiftDetail | null> {
  const store = await db.pharmacyStores.get(storeId);
  if (!store) return null;
  const [area, staff, assignments] = await Promise.all([db.pharmacyAreas.get(store.areaId), db.shiftStaff.where('storeId').equals(storeId).toArray(), db.shiftAssignments.where('storeId').equals(storeId).toArray()]);
  return {
    store,
    area: area ?? null,
    staff: staff.sort((a, b) => a.position.localeCompare(b.position)),
    assignments
  };
}

// ─── アラート ────────────────────────────────────────────────
const SEVERITY_ORDER: Record<ShiftAlert['severity'], number> = {
  critical: 0,
  warning: 1
};

/** シフトアラートを取得（既定で resolved 以外）。severity → 日付順。 */
export async function listShiftAlerts(params?: {
  storeId?: string;
  includeResolved?: boolean;
}): Promise<ShiftAlert[]> {
  let alerts = await db.shiftAlerts.toArray();
  if (params?.storeId) {
    alerts = alerts.filter(a => a.storeId === params.storeId);
  }
  if (!params?.includeResolved) {
    alerts = alerts.filter(a => a.status !== 'resolved');
  }
  return alerts.sort((a, b) => {
    const s = SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity];
    return s !== 0 ? s : a.date.localeCompare(b.date);
  });
}

// ─── 書き込み（作成体験 / 縦串） ────────────────────────────
export type UpsertAssignmentInput = {
  id?: string;
  storeId: string;
  staffId: string;
  date: string;
  startH: number;
  endH: number;
  breakStartH: number | null;
  breakEndH: number | null;
  position: ShiftPosition;
  note?: string;
};

/** シフト割当の作成・更新。 */
export async function upsertAssignment(input: UpsertAssignmentInput): Promise<string> {
  const id = input.id ?? `asg-rt-${crypto.randomUUID()}`;
  const row: ShiftAssignment = {
    id,
    storeId: input.storeId,
    staffId: input.staffId,
    date: input.date,
    startH: input.startH,
    endH: input.endH,
    breakStartH: input.breakStartH,
    breakEndH: input.breakEndH,
    position: input.position,
    note: input.note ?? ''
  };
  await db.shiftAssignments.put(row);
  return id;
}
export async function deleteAssignment(id: string): Promise<void> {
  await db.shiftAssignments.delete(id);
}

/** 指定店舗の対象月（YYYY-MM）の割当を全削除。 */
export async function clearMonth(storeId: string, monthPrefix: string): Promise<number> {
  const rows = await db.shiftAssignments.where('storeId').equals(storeId).filter(a => a.date.startsWith(monthPrefix)).toArray();
  await db.shiftAssignments.bulkDelete(rows.map(r => r.id));
  return rows.length;
}

/** 自動生成のオプション（シフト希望の反映・対象外スタッフの除外）。 */
export type AutoGenerateOptions = {
  /** 反映するシフト希望（preferred は配置、off は除外、limited は勤務窓を制限）。 */
  requests?: ShiftRequestDraft[];
  /** 生成対象から外すスタッフ ID（店長など、編成する本人を除外する用途）。 */
  excludeStaffIds?: string[];
};

/**
 * 月次シフトを自動生成（作成体験の mock）。
 * 既存の当月割当を一度クリアし、雇用形態の既定パターンを基に、
 * シフト希望（preferred / off / limited）を反映して埋める。
 * options 未指定時は従来どおり雇用形態のみで生成する（work-schedule からの呼び出し互換）。
 */
export async function autoGenerateMonth(storeId: string, monthPrefix: string, options?: AutoGenerateOptions): Promise<number> {
  await clearMonth(storeId, monthPrefix);
  const store = await db.pharmacyStores.get(storeId);
  const openH = store?.openHour ?? 9;
  const closeH = store?.closeHour ?? 22;
  const clampToOpen = (h: number): number => Math.max(openH, Math.min(closeH, h));
  const excludeStaffIds = new Set(options?.excludeStaffIds ?? []);
  const staff = (await db.shiftStaff.where('storeId').equals(storeId).toArray()).filter(s => !excludeStaffIds.has(s.id));

  // staffId → (date → 希望エントリ) の索引。
  const requestByStaff = new Map<string, Map<string, ShiftRequestDraft['entries'][number]>>();
  for (const req of options?.requests ?? []) {
    const byDate = new Map<string, ShiftRequestDraft['entries'][number]>();
    for (const entry of req.entries) byDate.set(entry.date, entry);
    requestByStaff.set(req.staffId, byDate);
  }
  const [yearStr, monthStr] = monthPrefix.split('-');
  const year = Number(yearStr);
  const month0 = Number(monthStr) - 1;
  const total = new Date(Date.UTC(year, month0 + 1, 0)).getUTCDate();
  const rows: ShiftAssignment[] = [];
  for (let day = 1; day <= total; day++) {
    const dow = (new Date(Date.UTC(year, month0, day)).getUTCDay() + 6) % 7;
    const date = `${monthPrefix}-${String(day).padStart(2, '0')}`;
    for (const s of staff) {
      const fullTime = s.employmentType === 'full_time';
      const baseWorks = fullTime ? dow <= 4 : dow === 0 || dow === 2 || dow === 4;
      const request = requestByStaff.get(s.id)?.get(date);
      let works = baseWorks;
      // 既定の勤務時間（営業時間にクランプ）。
      let startH = clampToOpen(fullTime ? openH : openH + 1);
      let endH = clampToOpen(fullTime ? openH + 8 : openH + 7);
      let note = '';
      if (request) {
        if (request.kind === 'off') {
          works = false;
        } else if (request.kind === 'preferred') {
          // 勤務希望: 既定で休みの日でも希望時間で配置する。
          works = true;
          startH = clampToOpen(request.startH);
          endH = clampToOpen(request.endH);
        } else {
          // limited: 提示された勤務可能窓に制限し、理由を引き継ぐ。
          works = true;
          startH = clampToOpen(request.startH);
          endH = clampToOpen(request.endH);
          note = request.note ?? '';
        }
      }
      if (!works || endH <= startH) continue;
      const duration = endH - startH;
      const longShift = duration > 6;
      const breakStartH = longShift ? clampToOpen(startH + Math.floor(duration / 2)) : null;
      rows.push({
        id: `asg-${s.id}-${date}`,
        storeId,
        staffId: s.id,
        date,
        startH,
        endH,
        breakStartH,
        breakEndH: breakStartH != null ? breakStartH + 1 : null,
        position: s.position,
        note
      });
    }
  }
  await db.shiftAssignments.bulkAdd(rows);
  return rows.length;
}

// ─── 応援要請（shiftCoverageRequest パッケージへの加算的書き込み） ──
const COVERAGE_PATTERN_ID = 'shiftCoverageRequest-pat-default';

/**
 * シフト応援要請を 1 件起票する。
 * seed の ensureShiftCoverageRequest（clear→再投入）とは別経路で、行を 1 件だけ追加する。
 */
async function appendCoverageRequest(input: {
  requester: string;
  supportStore: string;
  position: string;
  anchorDate: string;
}): Promise<string> {
  const id = `coverage-rt-${crypto.randomUUID()}`;
  const existing = await db.attendanceOpPackageInstanceItems.where('patternId').equals(COVERAGE_PATTERN_ID).count();
  const stageCol = (code: string, name: string, filled: number, required: number) => ({
    largeCategoryCode: 'coverage_flow',
    largeCategoryName: '対応状況',
    mediumCategoryCode: code,
    mediumCategoryName: name,
    inputFieldCount: filled,
    requiredFieldCount: required,
    hasIncludeFile: false
  });
  await db.attendanceOpPackageInstanceItems.add({
    id,
    patternId: COVERAGE_PATTERN_ID,
    sortOrder: existing,
    row: {
      id,
      completedOperationStepCount: 1,
      totalOperationStepCount: 3,
      target: {
        targetType: 'place',
        targetId: `store-${id}`,
        targetName: input.requester,
        targetAvatarUrl: ''
      },
      anchorDate: input.anchorDate,
      attributes: {
        supportStore: input.supportStore,
        position: input.position
      },
      // 起票直後＝要請のみ完了（回答・充足は未）
      columns: [stageCol('stage_request', '要請', 1, 1), stageCol('stage_response', '回答', 0, 1), stageCol('stage_fulfilled', '充足', 0, 1)]
    }
  });
  const pkg = await db.attendanceOpPackages.get('shiftCoverageRequest');
  if (pkg) {
    await db.attendanceOpPackages.update('shiftCoverageRequest', {
      inProgressCount: pkg.inProgressCount + 1
    });
  }
  return id;
}

/**
 * アラートを起点に応援要請を起票し、アラートを「要請済み」に更新する（縦串）。
 */
export async function resolveAlertViaCoverage(alertId: string, supportStoreId: string): Promise<string | null> {
  const alert = await db.shiftAlerts.get(alertId);
  if (!alert) return null;
  const supportStore = await db.pharmacyStores.get(supportStoreId);
  const coverageId = await appendCoverageRequest({
    requester: alert.storeName,
    supportStore: supportStore?.name ?? '新宿店',
    position: alert.position ? POSITION_LABEL_JA[alert.position] : '薬剤師',
    anchorDate: alert.date
  });
  await db.shiftAlerts.update(alertId, {
    status: 'requested',
    linkedCoverageRequestId: coverageId
  });
  return coverageId;
}

// ─── helpers ─────────────────────────────────────────────────
function groupBy<T>(items: T[], keyFn: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const item of items) {
    const key = keyFn(item);
    const arr = map.get(key);
    if (arr) arr.push(item);else map.set(key, [item]);
  }
  return map;
}